/**
 * Pro pricing for Boost-rendered product cards.
 *
 * Bold's pr.js keeps only one group of products priced per page load: it asks its
 * rules API for 15 product ids at a time, and whichever group it processes last is
 * the only one whose cards keep the discount. Every other card silently renders at
 * retail, with no error. Collection grids are always over that threshold, so their
 * cards are priced here instead and buildPrice() in boost-pfs-filter.js leaves the
 * `money` class off them so pr.js ignores them entirely.
 *
 * PDPs, cart and checkout are single-group, so they stay with Bold. Bold is also
 * what actually charges the customer, so the adjustment is always read from its own
 * rule response rather than hardcoded here — a rule edited in the Bold admin has to
 * move the storefront with it. Any rule carrying a condition this doesn't model is
 * skipped, which leaves the card at retail rather than showing a price that would
 * not be honoured at checkout.
 */
(function () {
  var payload = document.getElementById('bold-platform-data');
  if (!payload) return;

  var customer = JSON.parse(payload.textContent).customer;
  var shop = JSON.parse(payload.textContent).shop;
  if (!customer.id || !customer.tags.length) return;

  var ENDPOINT = 'https://api.boldcommerce.com/go_pre/pricerules/' + shop.permanent_domain + '/rulesets';
  var FILTERS = customer.tags
    .map(function (tag) { return 'eq(CUSTOMER_GROUP:' + tag + ')'; })
    .concat('like(CUSTOMER_ID:' + customer.id + ')')
    .map(function (condition) { return 'filter[]=' + encodeURIComponent(condition); })
    .join('&');

  // Our own batch size. Unlike pr.js every response is kept against the products it
  // was requested for, so batching never costs a card its discount.
  var BATCH_SIZE = 25;

  var rulesets = {};
  var requested = {};

  function toCents(text) {
    return Math.round(parseFloat(text.replace(/,/g, '')) * 100);
  }

  function format(original, cents) {
    var decimals = (original.split('.')[1] || '').length;
    var parts = (cents / 100).toFixed(decimals).split('.');
    return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (parts[1] ? '.' + parts[1] : '');
  }

  // Mirrors Bold's own percent/relative adjustments. Returns null for an action type
  // we don't model, which drops the whole rule.
  function adjust(cents, action) {
    if (action.type === 'PRICE_ADJUST_PERCENT') return cents + Math.round((cents / 100) * Number(action.value));
    if (action.type === 'PRICE_ADJUST_RELATIVE') return cents + Number(action.value);
    return null;
  }

  function selects(ruleset, productId, variantId) {
    if (ruleset.expiry_date && new Date(ruleset.expiry_date) < new Date()) return false;
    if (ruleset.product_selection.type === 'SHOPIFY_PRODUCTS_ALL') return true;
    return (ruleset.product_selection.products || []).some(function (entry) {
      if (String(entry.product_id) !== productId) return false;
      return !entry.variant_id || String(entry.variant_id) === variantId;
    });
  }

  function matchesCustomer(rule) {
    return rule.conditions.every(function (condition) {
      return condition.type === 'CUSTOMER_GROUP' && customer.tags.indexOf(condition.value) !== -1;
    });
  }

  // Bold picks the lowest priority number first, then the lowest price within it.
  function priceFor(productId, variantId, cents) {
    var best = null;
    var bestPriority = Infinity;

    rulesets[productId].forEach(function (ruleset) {
      if (!selects(ruleset, productId, variantId)) return;

      ruleset.rules.forEach(function (rule) {
        if (!matchesCustomer(rule)) return;

        var result = cents;
        for (var i = 0; i < rule.actions.length; i++) {
          var next = adjust(result, rule.actions[i]);
          if (next === null) return;
          result = next;
        }

        if (best === null || ruleset.priority < bestPriority ||
            (ruleset.priority === bestPriority && result < best)) {
          best = result;
          bestPriority = ruleset.priority;
        }
      });
    });

    return best === null ? cents : best;
  }

  function resolve(element) {
    var original = element.textContent.match(/[\d.,]+/);

    if (original) {
      var cents = toCents(original[0]);
      var priced = priceFor(
        element.getAttribute('data-pro-product-id'),
        element.getAttribute('data-pro-variant-id'),
        cents
      );

      if (priced < cents) {
        element.textContent = element.textContent.replace(/[\d.,]+/, format(original[0], priced));
        var cut = element.parentElement.querySelector('.price-cut');
        if (cut) cut.classList.add('price-cut--show');
      }
    }

    element.classList.add('pro-price--resolved');
  }

  function fetchRules(ids) {
    return fetch(ENDPOINT + '?products=' + ids.join(',') + '&' + FILTERS)
      .then(function (response) { return response.json(); })
      .then(function (body) {
        ids.forEach(function (id) { rulesets[id] = body.rulesets || []; });
      });
  }

  function scan() {
    var needed = [];

    Array.prototype.forEach.call(
      document.querySelectorAll('.pro-price:not(.pro-price--resolved)'),
      function (element) {
        var id = element.getAttribute('data-pro-product-id');
        if (rulesets[id]) return resolve(element);
        if (!requested[id] && needed.indexOf(id) === -1) needed.push(id);
      }
    );

    for (var i = 0; i < needed.length; i += BATCH_SIZE) {
      var batch = needed.slice(i, i + BATCH_SIZE);
      batch.forEach(function (id) { requested[id] = true; });
      fetchRules(batch).then(scan);
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      scan();
    });
  }

  function start() {
    scan();
    // Boost re-renders the whole grid on filter, sort and paginate.
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
